/**
 * Round 18 A34 -- the plans remediating a control, and their letters.
 *
 * 3.1.1-CR7's drawer, reached from the letter's strip, the plan and the
 * cycle, named neither the plan that remediates it nor the letter: its
 * only link was "Learn more". A control on no plan renders nothing.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { Bdi } from "shared/components/Bdi";
import { stateLabel } from "shared/vocab/workflowState";

export interface ControlPlan {
  cap_id: string;
  title: string;
  state: string;
  letter_id: string;
  letter_title: string;
  internal: boolean;
}

export function ControlPlansBlock({ controlId }: { controlId: string }) {
  const { t } = useTranslation();
  const plans = useQuery({
    queryKey: ["framework", "control-plans", controlId],
    queryFn: () =>
      api.get<ControlPlan[]>(
        `/v1/framework/controls/${encodeURIComponent(controlId)}/plans`,
      ),
    staleTime: 30_000,
  });
  const rows = Array.isArray(plans.data) ? plans.data : [];
  if (rows.length === 0) return null;
  return (
    <section data-testid="drawer-control-plans" className="text-xs text-slate-700">
      <span className="font-semibold text-slate-600">
        {t("assessmentGrid.remediatedBy")}
      </span>{" "}
      {rows.map((p, i) => (
        <span key={p.cap_id} data-testid="drawer-control-plan">
          {i > 0 && "; "}
          <Link
            to={`/framework/caps/${p.cap_id}`}
            className="font-medium text-brand-brassDark hover:underline"
          >
            <Bdi>{p.title}</Bdi>
          </Link>{" "}
          {t("assessmentGrid.planState", { state: stateLabel(p.state) })}{" "}
          {p.internal ? (
            t("assessmentGrid.raisedInternally")
          ) : (
            <>
              {t("assessmentGrid.fromLetter")}{" "}
              <Link
                to={`/framework/letters/${p.letter_id}`}
                className="text-brand-brassDark hover:underline"
              >
                <Bdi>{p.letter_title}</Bdi>
              </Link>
            </>
          )}
        </span>
      ))}
    </section>
  );
}
